'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { ArrowsInSimple, ArrowsOutSimple, Bug } from '@phosphor-icons/react';
import { Box, Dialog, IconButton, Stack, StateCard, Tooltip } from '@unity/cloud-ui';
import { useTheme } from '@unity/cloud-ui/styles';
import { MermaidConfig } from './initialize';

const MermaidContent = (props) => {
  const { toggleDialog, isDialogOpen, ref, renderingError } = props;
  const t = useTranslations();

  return (
    <Box py={1}>
      {!renderingError && (
        <Stack alignItems="flex-end" paddingX={1}>
          <Tooltip title={t('mdx:code:code-wrapper:toggle-fullscreen')} placement="top">
            <IconButton aria-label={t('mdx:code:code-wrapper:toggle-fullscreen')} onClick={toggleDialog}>
              {isDialogOpen ? <ArrowsInSimple weight="fill" size={24} /> : <ArrowsOutSimple weight="fill" size={24} />}
            </IconButton>
          </Tooltip>
        </Stack>
      )}
      <Box
        ref={ref}
        sx={{
          display: renderingError ? 'none' : 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      />
      <Box>{renderingError && <StateCard title={t('mdx:mermaid:mermaid-render-error')} icon={Bug} />}</Box>
    </Box>
  );
};

export const Mermaid = (props: { children?: string }) => {
  const { children } = props;
  const { palette } = useTheme();
  const diagramId = React.useId();
  const ref = React.useRef<HTMLDivElement>(null);
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const [renderingError, setRenderingError] = React.useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const toggleDialog = () => {
    setIsDialogOpen((prevState) => !prevState);
  };

  React.useEffect(() => {
    const drawDiagram = async () => {
      try {
        const graphDefinition = children?.replace(/`/g, '\\`');
        const graphWithTheme = `%%{
          init: {
            'theme': 'base',
            'themeVariables': {
              'primaryColor': '${palette.primary.lighter}',
              'primaryTextColor': '${palette.text.primary}',
              'primaryBorderColor': '${palette.default.outlinedBorder}',
              'lineColor': '${palette.primary.main}',
              'background': '${palette.background.default}',
              'secondaryColor': '${palette.chart.series1.solid}',
              'secondaryTextColor': '${palette.text.primary}',
              'tertiaryColor': '${palette.common.inverseWhite}',
              'labelTextColor': 'white',
              'pieStrokeWidth': 1,
              'pieOuterStrokeWidth': 1,
              'pie1': '${palette.chart.series1.solid}',
              'pie2': '${palette.chart.series2.solid}',
              'pie3': '${palette.chart.series3.solid}',
              'pie4': '${palette.chart.series4.solid}',
              'pie5': '${palette.chart.series5.solid}',
              'pie6': '${palette.chart.series6.solid}',
              'pie7': '${palette.chart.series7.solid}',
              'pie8': '${palette.chart.series8.solid}',
              'pie9': '${palette.chart.series9.solid}',
              'pie10': '${palette.chart.series10.solid}',
              'pie11': '${palette.chart.series11.solid}',
              'pie12': '${palette.chart.series12.solid}'
            }
          }
        }%%${graphDefinition}`;

        const { svg } = await MermaidConfig.get().render(`mermaid-${isDialogOpen}-${diagramId}`, graphWithTheme);

        if (!isDialogOpen && ref.current) {
          ref.current.innerHTML = svg;
        } else if (isDialogOpen && dialogRef.current) {
          dialogRef.current.innerHTML = svg;
        }
        setRenderingError(false);
      } catch (_) {
        setRenderingError(true);
      }
    };

    drawDiagram();
  }, [children, palette, isDialogOpen, diagramId]);

  const contentProps = {
    isDialogOpen,
    toggleDialog,
    renderingError,
  };

  return (
    <>
      <MermaidContent {...contentProps} ref={ref} />
      <Dialog fullScreen open={isDialogOpen} onClose={toggleDialog} sx={{ zIndex: 2101 }}>
        <MermaidContent {...contentProps} ref={dialogRef} />
      </Dialog>
    </>
  );
};
